import axios from "axios";
import { API_CONFIG } from "./configConstants";
import { requestForToken } from "./firebase";
import store from "../store";
import { setNotificationCount } from "../store/slices/countState";

const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear invalid token and redirect to login
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminData");
      localStorage.removeItem("userId");
      localStorage.removeItem("user_Id");

      // Redirect to login page
      window.location.href = "/login";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Utility functions
export const getAuthToken = () => {
  return localStorage.getItem("adminToken") || "";
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem("adminData");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

export const logout = async () => {
  store.dispatch(setNotificationCount(0));

  // Clear localStorage
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminData");
  localStorage.removeItem("userId");
  localStorage.removeItem("user_Id");

  const token = await requestForToken();
  if (token) {
    await authAPI.logout({ token: token });
  }

  // Redirect to login
  window.location.href = "/login";
};

// Generic API request function with retry logic
const apiRequest = async (endpoint, options = {}) => {
  const {
    method = "POST",
    data = {},
    params = {},
    headers = {},
    includeAuth = true,
    retryCount = 0,
  } = options;

  try {
    const config = {
      method,
      url: endpoint,
      data: method !== "GET" ? data : undefined,
      params: method === "GET" ? data : params,
      headers: {
        ...(includeAuth && { Authorization: `Bearer ${getAuthToken()}` }),
        ...headers,
      },
    };

    const response = await apiClient(config);
    return response?.data;
  } catch (error) {
    console.log(`API Request Error (${endpoint}):`, error);

    // Retry logic for network errors
    if (
      retryCount < API_CONFIG.RETRY_ATTEMPTS &&
      (!error.response || error.code === "ECONNABORTED")
    ) {
      await new Promise((resolve) =>
        setTimeout(resolve, API_CONFIG.RETRY_DELAY)
      );
      return apiRequest(endpoint, { ...options, retryCount: retryCount + 1 });
    }

    // Handle different types of errors
    if (error.response) {
      return {
        success: false,
        status: error.response.status,
        message: error.response.data?.message || "Request failed",
        data: error.response.data,
      };
    } else if (error.request) {
      return {
        success: false,
        status: 0,
        message: "Network error",
      };
    } else {
      return {
        success: false,
        status: 0,
        message: error.message || "An unexpected error occurred",
      };
    }
  }
};

// Authentication APIs
export const authAPI = {
  login: async (credentials) => {
    return await apiRequest("admin/web/login", {
      method: "POST",
      data: credentials,
      includeAuth: false,
    });
  },

  logout: async (requestData = {}) => {
    return await apiRequest("admin/logout", {
      method: "POST",
      includeAuth: true,
      data: requestData,
    });
  },
};

// Dashboard APIs
export const dashboardAPI = {
  getData: async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return {
        success: false,
        message: "User ID not found. Please login again.",
      };
    }

    return await apiRequest("admin/dashboard/data/get", {
      method: "POST",
      data: { user_id: userId },
    });
  },
};

// Profile APIs
export const profileAPI = {
  get: async () => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "default_user_id") {
      return {
        success: false,
        message: "User ID not found. Please login again.",
      };
    }

    return await apiRequest("admin/profile/get", {
      method: "POST",
      data: { user_id: userId },
    });
  },

  changePassword: async (passwordData) => {
    return await apiRequest("admin/profile/update", {
      method: "POST",
      data: passwordData,
    });
  },
};

// Brand APIs
export const brandAPI = {
  add: async (brandData) => {
    return await apiRequest("admin/brand/add", {
      method: "POST",
      data: brandData,
    });
  },

  getAll: async (reqObj) => {
    return await apiRequest("brand/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  update: async (brandData) => {
    return await apiRequest("admin/brand/update", {
      method: "POST",
      data: brandData,
    });
  },

  delete: async (brandData) => {
    return await apiRequest("admin/brand/delete", {
      method: "POST",
      data: brandData,
    });
  },
};

// Category APIs
export const categoryAPI = {
  add: async (categoryData) => {
    return await apiRequest("admin/category/add", {
      method: "POST",
      data: categoryData,
    });
  },

  getAll: async (reqObj) => {
    return await apiRequest("admin/category/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  update: async (categoryData) => {
    return await apiRequest("admin/category/update", {
      method: "POST",
      data: categoryData,
    });
  },

  delete: async (categoryData) => {
    return await apiRequest("admin/category/delete", {
      method: "POST",
      data: categoryData,
    });
  },

  updatePositions: async (positionsData) => {
    return await apiRequest("admin/category/positions/update", {
      method: "POST",
      data: positionsData,
    });
  },
};

// Product APIs
export const productAPI = {
  getAll: async (reqObj) => {
    return await apiRequest("admin/product/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  getProductReport: async (reqObj) => {
    return await apiRequest("admin/product/get/all/report", {
      method: "POST",
      data: reqObj,
    });
  },

  getById: async (productId) => {
    return await apiRequest("product/details/get", {
      method: "POST",
      data: { product_id: productId },
    });
  },

  add: async (productData) => {
    return await apiRequest("admin/product/add", {
      method: "POST",
      data: productData,
    });
  },

  update: async (productData) => {
    return await apiRequest("admin/product/update", {
      method: "POST",
      data: productData,
    });
  },

  updateStatus: async (statusData) => {
    return await apiRequest("admin/product/update", {
      method: "POST",
      data: statusData,
    });
  },

  delete: async (productData) => {
    return await apiRequest("admin/product/delete", {
      method: "POST",
      data: productData,
    });
  },

  uploadImage: async (imageData) => {
    return await apiRequest("admin/product/image/upload", {
      method: "POST",
      data: imageData,
    });
  },
  deleteImage: async (data) => {
    return await apiRequest("admin/product/image/delete", {
      method: "POST",
      data: data,
    });
  },
  getProductReviewAndRating: async (data) => {
    return await apiRequest("admin/product/ratingreview/get", {
      method: "POST",
      data: data,
    });
  },
  updateProductRatingReview: async (data) => {
    return await apiRequest("admin/product/ratingreview/update", {
      method: "POST",
      data: data,
    });
  },
};

// Order APIs
export const orderAPI = {
  getAll: async (reqObj) => {
    return await apiRequest("admin/order/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  getById: async (orderId) => {
    return await apiRequest("order/details/get", {
      method: "POST",
      data: { order_id: orderId },
    });
  },

  updateStatus: async (statusData) => {
    return await apiRequest("admin/order/update/status", {
      method: "POST",
      data: statusData,
    });
  },
};

// Salesperson APIs
export const salespersonAPI = {
  getAll: async (filters = {}) => {
    return await apiRequest("admin/salesperson/get/all", {
      method: "POST",
      data: filters,
    });
  },

  add: async (salespersonData) => {
    return await apiRequest("admin/salesperson/add", {
      method: "POST",
      data: salespersonData,
    });
  },

  update: async (salespersonData) => {
    return await apiRequest("admin/salesperson/update", {
      method: "POST",
      data: salespersonData,
    });
  },

  delete: async (salespersonData) => {
    return await apiRequest("admin/salesperson/delete", {
      method: "POST",
      data: salespersonData,
    });
  },
  updatePositions: async (salespersonData) => {
    return await apiRequest("admin/salespersons/position/update", {
      method: "POST",
      data: salespersonData,
    });
  },
};

// User APIs
export const userAPI = {
  getAll: async (filters = {}) => {
    const data = {};
    if (filters.user_type) data.user_type = filters.user_type;
    if (filters.search) data.search = filters.search;
    if (filters.active !== undefined) data.active = filters.active;

    return await apiRequest("user/get/all", {
      method: "POST",
      data,
    });
  },

  updateStatus: async (statusData) => {
    return await apiRequest("user/change/active/status", {
      method: "POST",
      data: statusData,
    });
  },
};

// Location APIs
export const locationAPI = {
  // Countries
  getCountries: async (reqObj) => {
    return await apiRequest("admin/country/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  addCountry: async (countryData) => {
    return await apiRequest("admin/country/add", {
      method: "POST",
      data: countryData,
    });
  },

  updateCountry: async (countryData) => {
    return await apiRequest("admin/country/update", {
      method: "POST",
      data: countryData,
    });
  },

  deleteCountry: async (countryData) => {
    return await apiRequest("admin/country/delete", {
      method: "POST",
      data: countryData,
    });
  },

  // States
  getStates: async (reqObj) => {
    return await apiRequest("state/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  addState: async (stateData) => {
    return await apiRequest("admin/state/add", {
      method: "POST",
      data: stateData,
    });
  },

  updateState: async (stateData) => {
    return await apiRequest("admin/state/update", {
      method: "POST",
      data: stateData,
    });
  },

  deleteState: async (stateData) => {
    return await apiRequest("admin/state/delete", {
      method: "POST",
      data: stateData,
    });
  },

  // Cities
  getCities: async (reqObj) => {
    return await apiRequest("city/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  addCity: async (cityData) => {
    return await apiRequest("admin/city/add", {
      method: "POST",
      data: cityData,
    });
  },

  updateCity: async (cityData) => {
    return await apiRequest("admin/city/update", {
      method: "POST",
      data: cityData,
    });
  },

  deleteCity: async (cityData) => {
    return await apiRequest("admin/city/delete", {
      method: "POST",
      data: cityData,
    });
  },

  // Pincodes
  getPincodes: async (reqObj) => {
    return await apiRequest("pincode/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  addPincode: async (pincodeData) => {
    return await apiRequest("admin/pincode/add", {
      method: "POST",
      data: pincodeData,
    });
  },

  updatePincode: async (pincodeData) => {
    return await apiRequest("admin/pincode/update", {
      method: "POST",
      data: pincodeData,
    });
  },

  deletePincode: async (pincodeData) => {
    return await apiRequest("admin/pincode/delete", {
      method: "POST",
      data: pincodeData,
    });
  },
};

// Banner APIs
export const bannerAPI = {
  getAll: async () => {
    return await apiRequest("admin/banner/get/all", {
      method: "POST",
      data: {},
    });
  },

  add: async (bannerData) => {
    return await apiRequest("admin/banner/add", {
      method: "POST",
      data: bannerData,
    });
  },

  update: async (bannerData) => {
    return await apiRequest("admin/banner/update", {
      method: "POST",
      data: bannerData,
    });
  },

  delete: async (bannerData) => {
    return await apiRequest("admin/banner/delete", {
      method: "POST",
      data: bannerData,
    });
  },
};

// Video APIs
export const videoAPI = {
  getAll: async () => {
    return await apiRequest("admin/video/get/all", {
      method: "POST",
      data: {},
    });
  },
  add: async (videoData) => {
    return await apiRequest("admin/video/add", {
      method: "POST",
      data: videoData,
    });
  },
  update: async (videoData) => {
    return await apiRequest("admin/video/update", {
      method: "POST",
      data: videoData,
    });
  },
  delete: async (videoData) => {
    return await apiRequest("admin/video/delete", {
      method: "POST",
      data: videoData,
    });
  },
};


// Bank Details APIs
export const bankAPI = {
  get: async () => {
    return await apiRequest("admin/bank/details/get", {
      method: "POST",
      data: {},
    });
  },

  update: async (bankData) => {
    return await apiRequest("admin/bank/details/update", {
      method: "POST",
      data: bankData,
    });
  },
};

// Settings APIs
export const settingsAPI = {
  get: async () => {
    const userId = localStorage.getItem("userId");
    return await apiRequest("admin/settings/get", {
      method: "POST",
      data: { user_id: userId },
    });
  },

  update: async (settingsData) => {
    const userId = localStorage.getItem("userId");
    return await apiRequest("admin/settings/update", {
      method: "POST",
      data: { ...settingsData, user_id: userId },
    });
  },
};

// Support APIs
export const supportAPI = {
  getTickets: async (requestData = {}) => {
    return await apiRequest("admin/support/ticket/get/all", {
      method: "POST",
      data: requestData,
    });
  },

  updateTicket: async (ticketData) => {
    return await apiRequest("support/ticket/update/status", {
      method: "POST",
      data: ticketData,
    });
  },

  getChats: async (ticketData) => {
    return await apiRequest("support/ticket/get/chats", {
      method: "POST",
      data: ticketData,
    });
  },

  addMessage: async (messageData) => {
    return await apiRequest("support/ticket/add/message", {
      method: "POST",
      data: messageData,
    });
  },
  getSupportTicketMessageCount: async () => {
    return await apiRequest("admin/support/ticket/message/count", {
      method: "POST",
      data: {},
    });
  },
  updateSupportTicketReadStatus: async () => {
    return await apiRequest("admin/support/ticket/status/update", {
      method: "POST",
      data: {},
    });
  },
};

// Notification APIs
export const notificationAPI = {
  getNotifications: async (requestData = {}) => {
    return await apiRequest("admin/notification/get/all", {
      method: "POST",
      data: requestData,
    });
  },

  markNotificationAsRead: async (requestData = {}) => {
    return await apiRequest("admin/notification/mark/read", {
      method: "POST",
      data: requestData,
    });
  },

  deleteAllNotifications: async () => {
    return await apiRequest("admin/notification/delete", {
      method: "POST",
    });
  },
};

// Chat APIs
export const chatAPI = {
  getUserList: async () => {
    return await apiRequest("chat/user/get/all", {
      method: "POST",
    });
  },
  getChattedUserList: async () => {
    return await apiRequest("admin/chat/users/get", {
      method: "POST",
    });
  },
  getChatHistory: async (reqObj) => {
    return await apiRequest("chat/history/get", {
      method: "POST",
      data: reqObj,
    });
  },
  addMessage: async (messageData) => {
    return await apiRequest("chat/message/add", {
      method: "POST",
      data: messageData,
    });
  },
  markChatMessageRead: async (messageData) => {
    return await apiRequest("chat/message/mark/read", {
      method: "POST",
      data: messageData,
    });
  },
};

// Coupon APIs
export const couponAPI = {
  add: async (couponData) => {
    return await apiRequest("admin/coupon/add", {
      method: "POST",
      data: couponData,
    });
  },

  getAll: async (reqObj) => {
    return await apiRequest("admin/coupon/get/all", {
      method: "POST",
      data: reqObj,
    });
  },

  delete: async (couponData) => {
    return await apiRequest("admin/coupon/delete", {
      method: "POST",
      data: couponData,
    });
  },
};

// Legacy function names for backward compatibility
export const loginAPI = authAPI.login;
export const logoutAPI = authAPI.logout;
export const getDashboardDataAPI = dashboardAPI.getData;
export const getAdminProfileAPI = profileAPI.get;
export const changePasswordAPI = profileAPI.changePassword;
export const addBrandAPI = brandAPI.add;
export const getBrandsAPI = brandAPI.getAll;
export const updateBrandAPI = brandAPI.update;
export const deleteBrandAPI = brandAPI.delete;
export const addCategoryAPI = categoryAPI.add;
export const getCategoriesAPI = categoryAPI.getAll;
export const updateCategoryAPI = categoryAPI.update;
export const deleteCategoryAPI = categoryAPI.delete;
export const updateCategoryPositionsAPI = categoryAPI.updatePositions;
export const getProductsAPI = productAPI.getAll;
export const getProductReportAPI = productAPI.getProductReport;
export const getProductDetailsAPI = productAPI.getById;
export const addProductAPI = productAPI.add;
export const uploadImageAPI = productAPI.uploadImage;
export const deleteImageAPI = productAPI.deleteImage;
export const getProductByIdAPI = productAPI.getById;
export const updateProductAPI = productAPI.update;
export const updateProductsAPI = productAPI.updateStatus;
export const deleteProductAPI = productAPI.delete;
export const getOrderAPI = orderAPI.getAll;
export const getOrderByIdAPI = orderAPI.getById;
export const updateOrderStatusAPI = orderAPI.updateStatus;
export const getSalespersonsAPI = salespersonAPI.getAll;
export const addSalespersonAPI = salespersonAPI.add;
export const updateSalespersonAPI = salespersonAPI.update;
export const deleteSalespersonAPI = salespersonAPI.delete;
export const getWholesaleUsersAPI = userAPI.getAll;
export const getAllUsersAPI = userAPI.getAll;
export const updateUserStatusAPI = userAPI.updateStatus;
export const getCountriesAPI = locationAPI.getCountries;
export const addCountryAPI = locationAPI.addCountry;
export const updateCountryAPI = locationAPI.updateCountry;
export const deleteCountryAPI = locationAPI.deleteCountry;
export const getStatesAPI = locationAPI.getStates;
export const addStateAPI = locationAPI.addState;
export const updateStateAPI = locationAPI.updateState;
export const deleteStateAPI = locationAPI.deleteState;
export const getCitiesAPI = locationAPI.getCities;
export const addCityAPI = locationAPI.addCity;
export const updateCityAPI = locationAPI.updateCity;
export const deleteCityAPI = locationAPI.deleteCity;
export const getPincodesAPI = locationAPI.getPincodes;
export const addPincodeAPI = locationAPI.addPincode;
export const updatePincodeAPI = locationAPI.updatePincode;
export const deletePincodeAPI = locationAPI.deletePincode;
export const getBannersAPI = bannerAPI.getAll;
export const addBannerAPI = bannerAPI.add;
export const updateBannerAPI = bannerAPI.update;
export const deleteBannerAPI = bannerAPI.delete;
export const getBankDetailsAPI = bankAPI.get;
export const updateBankDetailsAPI = bankAPI.update;
export const getGeneralSettingsAPI = settingsAPI.get;
export const updateGeneralSettingsAPI = settingsAPI.update;
export const getSupportTicketsAPI = supportAPI.getTickets;
export const updateSupportTicketAPI = supportAPI.updateTicket;
export const getChatsAPI = supportAPI.getChats;
export const addMessageAPI = supportAPI.addMessage;
export const getAllNotificationsAPI = notificationAPI.getNotifications;
export const markNotificationAsReadAPI = notificationAPI.markNotificationAsRead;
export const deleteAllNotificationsAPI = notificationAPI.deleteAllNotifications;
export const getChatUserListAPI = chatAPI.getUserList;
export const getChattedUserListAPI = chatAPI.getChattedUserList;
export const getChatHistoryAPI = chatAPI.getChatHistory;
export const addChatMessageAPI = chatAPI.addMessage;
export const markChatMessageReadAPI = chatAPI.markChatMessageRead;
export const getSupportTicketMessageCountAPI = supportAPI.getSupportTicketMessageCount;
export const updateSupportTicketReadStatusAPI = supportAPI.updateSupportTicketReadStatus;
export const getProductReviewAndRatingAPI = productAPI.getProductReviewAndRating;
export const updateProductRatingReviewAPI = productAPI.updateProductRatingReview;
export const updateSalespersonsPositionsAPI = salespersonAPI.updatePositions;
export const getVideosAPI = videoAPI.getAll;
export const addVideoAPI = videoAPI.add;
export const updateVideoAPI = videoAPI.update;
export const deleteVideoAPI = videoAPI.delete;
export const addCouponAPI = couponAPI.add;
export const getCouponsAPI = couponAPI.getAll;
export const deleteCouponAPI = couponAPI.delete;




